import { Button } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import {
  GROWTH_ENGINE_DISMISS_LABEL,
  GROWTH_ENGINE_SNOOZE_LABEL,
  getNudgeCardPropsFromRecommendation,
} from 'src/components/growthEngine/GrowthEngineNudgeCard'
import {
  fetchGrowthEngineRecommendations,
  postGrowthEngineRecommendationInteraction,
} from 'src/utils/growthEngineApi'
import { resolveGrowthEngineCtaPath } from 'src/utils/growthEngineCtaRoutes'
import {
  clearGrowthEngineFillBasketFollowUp,
  hasGrowthEngineFillBasketFollowUp,
} from 'src/utils/growthEngineFollowUpSession'

/** Session-scoped intentIds already recorded as `shown` for this strip. */
const shownFollowUpIntentIdsThisSession = new Set()

/**
 * Quiet follow-up strip on shopping lists / catalogue.
 * Same cool-off interactions as the primary card.
 *
 * Gate (OR): session after Place an order (`open_orders`) OR empty basket —
 * Free venues only. Hide once the basket has lines and the session flag is gone.
 */
const GrowthEngineFollowUpStrip = ({
  venueId,
  currentPlan,
  /** 'shopping_lists' | 'catalogue' */
  surface,
  className,
  'data-testid': dataTestId = 'growth-engine-follow-up-strip',
}) => {
  const history = useHistory()
  const location = useLocation()
  const basket = useSelector(state => state.orders.basket)
  const basketEmpty = !basket || basket.length === 0
  const sessionFollowUp = hasGrowthEngineFillBasketFollowUp(venueId)

  const [recommendation, setRecommendation] = useState(null)
  const [busy, setBusy] = useState(false)
  const fetchGeneration = useRef(0)

  const clearRecommendation = useCallback(() => {
    setRecommendation(null)
  }, [])

  // Prefer empty basket; session alone still allows a brief follow-up after CTA.
  const mayFetch =
    Boolean(venueId) &&
    Boolean(surface) &&
    (!currentPlan || currentPlan === 'Free') &&
    (basketEmpty || sessionFollowUp)

  useEffect(() => {
    if (!mayFetch) {
      setRecommendation(null)
      return undefined
    }

    const generation = ++fetchGeneration.current
    let cancelled = false

    ;(async () => {
      try {
        const result = await fetchGrowthEngineRecommendations(venueId, {
          platform: 'dashboard',
          surface,
        })
        if (cancelled || generation !== fetchGeneration.current) return

        const next = result.recommendation || null
        if (next?.intentId !== 'activate_fill_basket') {
          setRecommendation(null)
          return
        }

        setRecommendation(next)

        if (
          next.intentId &&
          !shownFollowUpIntentIdsThisSession.has(next.intentId)
        ) {
          shownFollowUpIntentIdsThisSession.add(next.intentId)
          try {
            await postGrowthEngineRecommendationInteraction(venueId, {
              intentId: next.intentId,
              interaction: 'shown',
              featureId: next.featureId,
              platform: 'dashboard',
              metadata: {
                variantIndex: next.variantIndex,
                surface: `dashboard_${surface}`,
              },
            })
          } catch (err) {
            console.warn('Growth Engine follow-up shown failed', err)
          }
        }
      } catch (err) {
        if (!cancelled && generation === fetchGeneration.current) {
          console.warn('Growth Engine follow-up failed', err)
          setRecommendation(null)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [mayFetch, venueId, surface, basketEmpty, sessionFollowUp])

  const postInteraction = useCallback(
    async (interaction, metadata = {}) => {
      if (!venueId || !recommendation?.intentId) return
      setBusy(true)
      try {
        await postGrowthEngineRecommendationInteraction(venueId, {
          intentId: recommendation.intentId,
          interaction,
          featureId: recommendation.featureId,
          platform: 'dashboard',
          metadata: {
            variantIndex: recommendation.variantIndex,
            surface: `dashboard_${surface}`,
            ...metadata,
          },
        })
      } finally {
        setBusy(false)
      }
    },
    [venueId, recommendation, surface],
  )

  const hideAndClearSession = useCallback(() => {
    clearGrowthEngineFillBasketFollowUp(venueId)
    clearRecommendation()
  }, [venueId, clearRecommendation])

  const handleSnooze = useCallback(async () => {
    try {
      await postInteraction('snoozed')
    } catch (err) {
      console.warn('Growth Engine follow-up snooze failed', err)
    }
    hideAndClearSession()
  }, [postInteraction, hideAndClearSession])

  const handleDismiss = useCallback(async () => {
    try {
      await postInteraction('dismissed')
    } catch (err) {
      console.warn('Growth Engine follow-up dismiss failed', err)
    }
    hideAndClearSession()
  }, [postInteraction, hideAndClearSession])

  const handleCta = useCallback(async () => {
    const action = recommendation?.cta?.action
    try {
      await postInteraction('clicked', { ctaAction: action || null })
    } catch (err) {
      console.warn('Growth Engine follow-up click failed', err)
    }

    const path = resolveGrowthEngineCtaPath(venueId, action)
    clearRecommendation()
    if (path && path !== location.pathname) {
      history.push(path)
    }
  }, [
    recommendation,
    postInteraction,
    venueId,
    clearRecommendation,
    history,
    location.pathname,
  ])

  const cardProps = getNudgeCardPropsFromRecommendation(recommendation)
  if (!cardProps) return null

  return (
    <Strip
      className={className}
      data-testid={dataTestId}
      role='region'
      aria-label={cardProps.title || 'Ordering tip'}
    >
      <Copy>
        {cardProps.title ? <Title>{cardProps.title}</Title> : null}
        {cardProps.body ? <Body>{cardProps.body}</Body> : null}
      </Copy>
      <Actions>
        {cardProps.ctaLabel ? (
          <Button
            type='link'
            onClick={handleCta}
            disabled={busy}
            data-testid='follow-up-cta'
          >
            {cardProps.ctaLabel}
          </Button>
        ) : null}
        <QuietLink
          type='link'
          onClick={handleSnooze}
          disabled={busy}
          data-testid='follow-up-snooze'
        >
          {GROWTH_ENGINE_SNOOZE_LABEL}
        </QuietLink>
        <QuietLink
          type='link'
          onClick={handleDismiss}
          disabled={busy}
          data-testid='follow-up-dismiss'
        >
          {GROWTH_ENGINE_DISMISS_LABEL}
        </QuietLink>
      </Actions>
    </Strip>
  )
}

const Strip = styled.div`
  background: ${({ theme }) => theme.colours?.sageWash || '#f6ffed'};
  border-radius: 8px;
  box-sizing: border-box;
  margin: 4px 0 16px;
  max-width: 720px;
  padding: 12px 16px;
  text-align: left;
  width: 100%;
`

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Title = styled.p`
  color: ${({ theme }) => theme.colours.bbqBlack || '#262626'};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
`

const Body = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey || '#595959'};
  font-size: 13px;
  line-height: 1.45;
  margin: 0;
  max-width: 62ch;
`

const Actions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0 4px;
  margin-top: 6px;
`

const QuietLink = styled(Button)`
  && {
    color: ${({ theme }) => theme.colours.oliveGrey || '#8c8c8c'};
    font-size: 13px;
    height: auto;
    padding: 0 8px;
  }

  &&:hover,
  &&:focus {
    color: ${({ theme }) => theme.colours.bbqBlack || '#595959'};
  }
`

export default GrowthEngineFollowUpStrip

/** Test helper — reset session shown-tracking between vitest cases. */
export function __resetGrowthEngineFollowUpStripSessionForTests() {
  shownFollowUpIntentIdsThisSession.clear()
}
